import axios from "axios";
import { getAccessToken, getCsrfToken, clearAccessToken, clearCsrfToken, clearRefreshToken } from "./tokenStore";
import { silentRefresh } from "./AuthApi/RefreshApi";

const UserHotelApi = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Read tokens from module-level store — never from localStorage
UserHotelApi.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    const csrf = getCsrfToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const method = config.method?.toLowerCase();
    if (csrf && ["post", "put", "patch", "delete"].includes(method)) {
      config.headers["x-csrf-token"] = csrf;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Track if a token refresh is in progress to prevent multiple simultaneous refreshes
let isRefreshing = false;
// Queue of requests that failed with 401 while refresh was in progress
let failedQueue = [];

// Process queued requests after successful refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor to handle token refresh on 401 errors
UserHotelApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and request hasn't been retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If refresh is already in progress, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return UserHotelApi(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const refreshSuccess = await silentRefresh();

        if (refreshSuccess) {
          // Get new access token
          const newToken = getAccessToken();

          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Process any queued requests
          processQueue(null, newToken);

          // Retry the original request
          return UserHotelApi(originalRequest);
        } else {
          // Refresh failed - clear tokens and reject
          clearAccessToken();
          clearCsrfToken();
          clearRefreshToken();
          processQueue(new Error("Token refresh failed"), null);
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh threw an error - clear tokens and reject
        clearAccessToken();
        clearCsrfToken();
        clearRefreshToken();
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Common reusable function
export const apiRequest = async (
  method,
  url,
  data = {},
  params = {},
  headers = {},
  signal,
  config = {}
) => {
  try {

    
    const response = await UserHotelApi({
      method,
      url,
      data,
      params,
      headers,
      signal,
      ...config,
    });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.debug("Request canceled:", error.message);
      return;
    }

    const customError = new Error(
      error.response?.data?.message || error.message,
    );

    customError.success = false;
    customError.status = error.response?.status;
    customError.data = error.response?.data;

    throw customError;
  }
};

// Helper wrappers
export const getData = (url, params = {}, headers = {}, signal, config = {}) =>
  apiRequest("get", url, {}, params, headers, signal, config);

export const postData = (url, data = {}, headers = {}, signal) =>
  apiRequest("post", url, data, {}, headers, signal);

export const putData = (url, data = {}, headers = {}, signal) =>
  apiRequest("put", url, data, {}, headers, signal);

export const patchData = (url, data = {}, headers = {}, signal) =>
  apiRequest("patch", url, data, {}, headers, signal);

export const deleteData = (url, params = {}, headers = {}, signal) =>
  apiRequest("delete", url, {}, params, headers, signal);

// Export the original instance
export default UserHotelApi;
